import { createContext, useContext, ReactNode, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { convertCurrency } from '@/lib/currencyConversion';
import { useAuth } from './AuthContext';

interface BudgetData {
  monthlyBudget: number;
  spent: number;
  remaining: number;
  currency: string;
  dailyBudget: number;
  daysLeft: number;
  percentUsed: number;
  isOverBudget: boolean;
  isOnTrack: boolean;
}

interface BudgetContextType {
  data: BudgetData | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => void;
}

const defaultBudgetData: BudgetData = {
  monthlyBudget: 1000,
  spent: 0,
  remaining: 1000,
  currency: 'EUR',
  dailyBudget: 0,
  daysLeft: 30,
  percentUsed: 0,
  isOverBudget: false,
  isOnTrack: true,
};

const BudgetContext = createContext<BudgetContextType | undefined>(undefined);

async function fetchBudgetData(userId: string): Promise<BudgetData> {
  // Fetch budget settings and expenses in parallel
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const firstDayStr = firstDay.toISOString().split('T')[0];

  const [budgetResult, expensesResult] = await Promise.all([
    supabase
      .from('budget_settings')
      .select('monthly_budget, currency')
      .eq('user_id', userId)
      .maybeSingle(),
    supabase
      .from('expenses')
      .select('amount, currency')
      .eq('user_id', userId)
      .gte('expense_date', firstDayStr),
  ]);

  const budgetData = budgetResult.data;
  const expenses = expensesResult.data;

  const monthlyBudget = budgetData?.monthly_budget ?? 1000;
  const currency = budgetData?.currency ?? 'EUR';

  // Calculate spent with currency conversion
  const spent = expenses?.reduce((sum, exp) => {
    const converted = convertCurrency(
      Number(exp.amount),
      exp.currency || 'EUR',
      currency
    );
    return sum + converted;
  }, 0) ?? 0;

  const remaining = monthlyBudget - spent;
  const percentUsed = (spent / monthlyBudget) * 100;
  const isOverBudget = remaining < 0;

  // Calculate days left in month
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const daysLeft = Math.max(1, Math.ceil((lastDayOfMonth.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  const dailyBudget = remaining > 0 ? remaining / daysLeft : 0;

  return {
    monthlyBudget,
    spent,
    remaining,
    currency,
    dailyBudget,
    daysLeft,
    percentUsed,
    isOverBudget,
    isOnTrack: !isOverBudget,
  };
}

export function BudgetProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['budget', user?.id],
    queryFn: () => fetchBudgetData(user!.id),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  const refresh = useCallback(() => {
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: ['budget', user.id] });
    }
  }, [user?.id, queryClient]);

  return (
    <BudgetContext.Provider value={{ 
      data: data ?? null, 
      isLoading, 
      error: error as Error | null,
      refresh 
    }}>
      {children}
    </BudgetContext.Provider>
  );
}

export function useBudget() {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudget must be used within BudgetProvider');
  }
  return context;
}
